/**
 * Admin Site Analytics
 * Traffic, pages, and geographic analytics via PostHog
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminApiGet } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowSquareOut,
  ChartBar,
  Clock,
  CursorClick,
  Eye,
  Globe,
  MapPin,
  Pulse,
  TrendUp,
  UsersThree,
  WarningCircle
} from '@phosphor-icons/react';
const SiteAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [pagesData, setPagesData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [range, setRange] = useState('30d');

  useEffect(() => {
    if (activeTab === 'overview') fetchOverview();
    if (activeTab === 'pages') fetchPages();
    if (activeTab === 'geography') fetchGeography();
  }, [activeTab, range]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/analytics/overview', { range });
      setOverviewData(data);
    } catch (error) {
      console.error('Error fetching overview:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/analytics/pages', { range });
      setPagesData(data);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Failed to load page analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchGeography = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/analytics/geography', { range });
      setGeoData(data);
    } catch (error) {
      console.error('Error fetching geography:', error);
      toast.error('Failed to load geography analytics');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBar },
    { id: 'pages', label: 'Pages', icon: Eye },
    { id: 'geography', label: 'Geography', icon: Globe },
  ];

  const showPostHogBanner = overviewData && !overviewData.config?.posthog_configured;

  if (loading && !overviewData && !pagesData && !geoData) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Site Analytics</h1>
          <p className="text-gray-400">Traffic and usage analytics for the platform</p>
        </div>

        {/* Range Selector */}
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </div>

      {/* PostHog Not Configured Banner */}
      {showPostHogBanner && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
          <WarningCircle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-yellow-500 font-semibold mb-1">Analytics not fully configured</h3>
            <p className="text-sm text-yellow-200/80">
              PostHog is not configured. Showing sample data only. Add POSTHOG_API_KEY to .env to enable real analytics.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'text-[#6366F1] border-b-2 border-[#6366F1]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab data={overviewData} loading={loading} />}
      {activeTab === 'pages' && <PagesTab data={pagesData} loading={loading} />}
      {activeTab === 'geography' && <GeoTab data={geoData} loading={loading} />}
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ data, loading }) => {
  if (loading) {
    return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6366F1] mx-auto"></div></div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Eye size={20} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Visits</p>
              <p className="text-2xl font-bold text-white">{data?.total_visits_30d?.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Last 30 days</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <Pulse size={20} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Active UsersThree</p>
              <p className="text-2xl font-bold text-white">{data?.active_users_24h}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Last 24 hours</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <UsersThree size={20} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-400">New Signups</p>
              <p className="text-2xl font-bold text-white">{data?.new_signups_30d}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Last 30 days</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
              <MapPin size={20} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Top Country</p>
              <p className="text-2xl font-bold text-white">{data?.top_country}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">By visits</p>
        </div>
      </div>

      {/* Traffic Chart */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <TrendUp size={18} />
          Traffic Over Time
        </h3>
        <div className="h-64 flex items-end justify-between gap-2">
          {data?.traffic_timeseries_30d?.slice(-30).map((item, index) => {
            const maxVisits = Math.max(...data.traffic_timeseries_30d.map(d => d.visits));
            const height = (item.visits / maxVisits) * 100;
            return (
              <div key={index} className="flex-1 flex flex-col items-center group">
                <div 
                  className="w-full bg-gradient-to-t from-[#6366F1] to-[#EC4899] rounded-t hover:opacity-80 transition-opacity relative"
                  style={{ height: `${height}%`, minHeight: '4px' }}
                  title={`${item.date}: ${item.visits} visits`}
                >
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs text-white bg-black/80 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {item.visits}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Sources */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <CursorClick size={18} />
          Top Traffic Sources
        </h3>
        <div className="space-y-3">
          {data?.sources?.map((source, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-medium">{source.source}</span>
                  <span className="text-gray-400 text-sm">{source.visits.toLocaleString()} visits</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#6366F1] to-[#EC4899]"
                    style={{ width: `${source.percent * 100}%` }}
                  />
                </div>
              </div>
              <span className="text-gray-400 text-sm ml-4 w-12 text-right">
                {(source.percent * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Pages Tab Component
const PagesTab = ({ data, loading }) => {
  if (loading) {
    return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6366F1] mx-auto"></div></div>;
  }

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
      <table className="w-full">
        <thead className="bg-white/5 border-b border-white/10">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Page</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Views</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Unique</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Avg Time</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Bounce Rate</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {data?.pages?.map((page, index) => (
            <tr key={index} className="hover:bg-white/5">
              <td className="px-6 py-4">
                <div>
                  <p className="text-sm font-medium text-white">{page.title}</p>
                  <p className="text-xs text-gray-400">{page.path}</p>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-white text-right font-semibold">{page.views.toLocaleString()}</td>
              <td className="px-6 py-4 text-sm text-white text-right">{page.unique.toLocaleString()}</td>
              <td className="px-6 py-4 text-sm text-white text-right">
                {page.avg_time_seconds ? `${Math.floor(page.avg_time_seconds / 60)}m ${page.avg_time_seconds % 60}s` : '–'}
              </td>
              <td className="px-6 py-4 text-sm text-white text-right">
                {page.bounce_rate ? `${(page.bounce_rate * 100).toFixed(0)}%` : '–'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Geography Tab Component
const GeoTab = ({ data, loading }) => {
  if (loading) {
    return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6366F1] mx-auto"></div></div>;
  }

  const maxVisits = data?.countries ? Math.max(...data.countries.map(c => c.visits)) : 0;

  return (
    <div className="space-y-6">
      {/* Top Countries Bar Chart */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Globe size={18} />
          Top Countries
        </h3>
        <div className="space-y-4">
          {data?.countries?.slice(0, 5).map((country, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">{country.country}</span>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm">{country.visits.toLocaleString()} visits</span>
                  <span className="text-gray-400 text-sm w-12 text-right">
                    {(country.percent * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#6366F1] to-[#EC4899]"
                  style={{ width: `${(country.visits / maxVisits) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* All Countries Table */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Country</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Visits</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {data?.countries?.map((country, index) => (
              <tr key={index} className="hover:bg-white/5">
                <td className="px-6 py-4 text-sm font-medium text-white">{country.country}</td>
                <td className="px-6 py-4 text-sm text-white text-right font-semibold">{country.visits.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-white text-right">{(country.percent * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SiteAnalytics;
